import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import { 
  Dns as DnsIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { generateDnsRecords as generateSharedDnsRecords } from '../../config/dns';
import SetupLayout from './SetupLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface DnsRecord {
  type: string;
  host: string;
  value: string;
  priority?: number;
  ttl?: string;
  verified?: boolean;
  description?: string;
  purpose?: string;
}

interface Registrar {
  name: string;
  logo: string;
  dnsUrl: string;
  color: string;
}

const registrars: Record<string, Registrar> = {
  godaddy: { name: 'GoDaddy', logo: '🌐', dnsUrl: 'https://dcc.godaddy.com/manage/dns', color: '#00a4a6' },
  namecheap: { name: 'Namecheap', logo: '🔶', dnsUrl: 'https://ap.www.namecheap.com/domains/domaincontrolpanel', color: '#de5833' },
  cloudflare: { name: 'Cloudflare', logo: '☁️', dnsUrl: 'https://dash.cloudflare.com/', color: '#f48120' },
  google: { name: 'Google Domains', logo: '🔍', dnsUrl: 'https://domains.google.com/registrar/', color: '#4285f4' },
  hostinger: { name: 'Hostinger', logo: '🏠', dnsUrl: 'https://hpanel.hostinger.com/domains/', color: '#673de6' },
  bluehost: { name: 'Bluehost', logo: '🔵', dnsUrl: 'https://my.bluehost.com/hosting/app#/domains', color: '#0046be' },
  bigrock: { name: 'BigRock', logo: '🪨', dnsUrl: 'https://myaccount.bigrock.in/servlet/AutoLoginServlet', color: '#ff6b00' },
  hostgator: { name: 'HostGator', logo: '🐊', dnsUrl: 'https://portal.hostgator.com/domain', color: '#0062cc' },
  name: { name: 'Name.com', logo: '📛', dnsUrl: 'https://www.name.com/account/domain', color: '#0066ff' },
  dynadot: { name: 'Dynadot', logo: '🔹', dnsUrl: 'https://www.dynadot.com/account/domain/name/server.html', color: '#3399cc' },
};

const DNSSetup: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([]);
  const [verificationStatus, setVerificationStatus] = useState<Record<string, boolean>>({});
  const [detectedRegistrar, setDetectedRegistrar] = useState<string | null>(null);
  const [nameservers, setNameservers] = useState<string[]>([]);

  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
  const domain = signupData.domain || 'example.com';

  // Derive the correct verification status key for each DNS record
  const getVerificationKey = (record: DnsRecord): string => {
    if (record.type === 'MX') return 'mx';
    if (record.type === 'A') return 'a';
    if (record.type === 'TXT') {
      if (record.host === '@' || record.value?.includes('v=spf1')) return 'spf';
      if (record.host?.includes('_domainkey')) return 'dkim';
      if (record.host === '_dmarc') return 'dmarc';
      if (record.host === '_smtp._tls') return 'tlsrpt';
    }
    return record.type.toLowerCase();
  };

  useEffect(() => {
    const storedData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
    if (!storedData.organization) {
      navigate('/signup');
    }
    fetchDnsRecords();
    detectRegistrar();
  }, [navigate]);

  // Fetch real DNS records from backend (including generated DKIM key)
  const fetchDnsRecords = async () => {
    setLoadingRecords(true);
    try {
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      const response = await axios.get(`${API_URL}/signup/dns-records/${currentData.domainId}?token=${currentData.token}`, { timeout: 15000 });
      
      if (response.data.success && response.data.data) {
        const data = response.data.data.data || response.data.data;
        if (data.records && data.records.length > 0) {
          setDnsRecords(data.records);
          if (data.status) {
            setVerificationStatus(data.status);
          }
        } else {
          // Fallback to lightweight endpoint
          await fetchDnsRecordsFallback();
        }
      } else {
        await fetchDnsRecordsFallback();
      }
    } catch (err) {
      console.error('Error fetching DNS records:', err);
      // Fallback to lightweight endpoint (no DNS verification, just records)
      await fetchDnsRecordsFallback();
    } finally {
      setLoadingRecords(false);
    }
  };

  // Lightweight fallback — fetches records by domain name without token/DNS checks
  const fetchDnsRecordsFallback = async () => {
    try {
      const response = await axios.get(`${API_URL}/signup/dns-records-by-domain/${domain}`, { timeout: 10000 });
      if (response.data?.success || response.data?.data) {
        const data = response.data.data?.data || response.data.data;
        if (data?.records && data.records.length > 0) {
          setDnsRecords(data.records);
          return;
        }
      }
    } catch (err) {
      console.error('Fallback DNS fetch failed:', err);
    }
    // Last resort: generated records without DKIM key
    generateFallbackRecords();
  };

  // Generate fallback records if API fails — uses shared CruvzMail DNS config
  const generateFallbackRecords = async () => {
    const records = generateSharedDnsRecords(domain);
    setDnsRecords(records);
  };

  const detectRegistrar = async () => {
    try {
      const response = await axios.get(`${API_URL}/dns/detect-registrar?domain=${domain}`);
      if (response.data.success && response.data.data) {
        const data = response.data.data.data || response.data.data;
        setNameservers(data.nameservers || []);
        setDetectedRegistrar(data.registrar || null);
      }
    } catch (err) {
      console.log('Could not detect registrar:', err);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyAllRecords = async () => {
    const recordsText = dnsRecords.map(r => 
      `Type: ${r.type}\nHost: ${r.host}\nValue: ${r.value}${r.priority ? `\nPriority: ${r.priority}` : ''}\n`
    ).join('\n---\n\n');
    
    try {
      await navigator.clipboard.writeText(recordsText);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 3000);
    } catch (err) {
      console.error('Failed to copy all:', err);
    }
  };

  const verifyDns = async () => {
    setVerifying(true);
    setError('');

    try {
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      const response = await axios.post(`${API_URL}/signup/verify-dns`, {
        domain: domain,
        domainId: currentData.domainId,
        token: currentData.token,
      });

      if (response.data.success || response.data.data) {
        const data = response.data.data?.data || response.data.data || response.data;
        const status = data.status || {};
        setVerificationStatus(status);
        const allVerified = Object.values(status).every(v => v === true);
        if (allVerified) {
          setSuccess('All DNS records verified successfully!');
        } else {
          const verified = Object.entries(status).filter(([_, v]) => v).map(([k]) => k);
          const pending = Object.entries(status).filter(([_, v]) => !v).map(([k]) => k);
          if (verified.length > 0) {
            setSuccess(`Verified: ${verified.join(', ')}. Pending: ${pending.join(', ')}`);
          } else {
            setError('DNS records not yet propagated. Please wait and try again (can take up to 48 hours).');
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'DNS verification failed. Records may take up to 48 hours to propagate.');
    } finally {
      setVerifying(false);
    }
  };

  const handleContinue = async () => {
    setLoading(true);
    const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
    sessionStorage.setItem('signupData', JSON.stringify({
      ...currentData,
      dnsConfigured: true,
    }));
    setTimeout(() => navigate('/setup/complete'), 500);
  };

  const handleSkip = () => {
    const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
    sessionStorage.setItem('signupData', JSON.stringify({
      ...currentData,
      dnsConfigured: false,
      skipDns: true,
    }));
    navigate('/setup/complete');
  };

  const openRegistrarDns = (registrarKey: string) => {
    const reg = registrars[registrarKey];
    if (reg) {
      let url = reg.dnsUrl;
      if (registrarKey === 'godaddy') {
        url = `https://dcc.godaddy.com/manage/${domain}/dns`;
      } else if (registrarKey === 'namecheap') {
        url = `https://ap.www.namecheap.com/domains/domaincontrolpanel/${domain}/advancedns`;
      } else if (registrarKey === 'cloudflare') {
        url = `https://dash.cloudflare.com/?to=/:account/${domain}/dns`;
      } else if (registrarKey === 'google') {
        url = `https://domains.google.com/registrar/${domain}/dns`;
      } else if (registrarKey === 'hostinger') {
        url = `https://hpanel.hostinger.com/domain/${domain}/dns`;
      }
      window.open(url, '_blank');
    }
  };

  return (
    <SetupLayout activeStep={5} backPath="/setup/admin-user">
      {/* Main Content */}
      <Box sx={{ flex: 1, py: 4 }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DnsIcon sx={{ fontSize: 30, color: '#dc2626' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                Configure DNS Records
              </Typography>
              <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                Add these records to your domain: <strong>{domain}</strong>
              </Typography>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

          {/* Info Alert */}
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Add these DNS records to your domain registrar (GoDaddy, Namecheap, Cloudflare, etc). DNS propagation takes 15 minutes to 48 hours.
          </Alert>

          {/* Copy All Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={copyAllRecords}
              startIcon={copiedAll ? <CheckIcon /> : <FileCopyIcon />}
              sx={{
                borderColor: copiedAll ? '#22c55e' : '#e5e7eb',
                color: copiedAll ? '#22c55e' : '#6b7280',
                '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' },
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {copiedAll ? 'Copied All Records!' : 'Copy All Records'}
            </Button>
          </Box>

          {/* DNS Table - FIRST */}
          <TableContainer sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e5e7eb', mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Host/Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif', width: '40%' }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>TTL</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Copy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRecords ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} sx={{ color: '#dc2626' }} />
                      <Typography sx={{ mt: 1, color: '#6b7280' }}>Loading DNS records...</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dnsRecords.map((record, index) => {
                    const isRecommended = record.description?.includes('Recommended') || record.purpose?.includes('Recommended') || ['CNAME', 'SRV'].includes(record.type) || record.host === '_smtp._tls';
                    const isFirstRecommended = isRecommended && (index === 0 || !((dnsRecords[index - 1] as any).description?.includes('Recommended') || (dnsRecords[index - 1] as any).purpose?.includes('Recommended') || ['CNAME', 'SRV'].includes(dnsRecords[index - 1].type) || dnsRecords[index - 1].host === '_smtp._tls'));
                    return (
                    <React.Fragment key={index}>
                      {index === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ bgcolor: '#fef2f2', py: 0.75, fontWeight: 700, color: '#dc2626', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', borderBottom: '2px solid #fecaca' }}>
                            REQUIRED — Must be added for email to work
                          </TableCell>
                        </TableRow>
                      )}
                      {isFirstRecommended && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ bgcolor: '#f0fdf4', py: 0.75, fontWeight: 700, color: '#166534', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', borderBottom: '2px solid #bbf7d0' }}>
                            RECOMMENDED — Improve deliverability &amp; auto-configuration
                          </TableCell>
                        </TableRow>
                      )}
                    <TableRow sx={{ '&:hover': { bgcolor: '#f1f5f9' } }}>
                      <TableCell>
                        <Chip 
                          label={record.type} 
                          size="small" 
                          sx={{ 
                            bgcolor: record.type === 'MX' ? '#dbeafe' : record.type === 'TXT' ? '#fef3c7' : record.type === 'CNAME' ? '#f3e8ff' : record.type === 'SRV' ? '#e0e7ff' : '#dcfce7', 
                            color: '#374151', 
                            fontWeight: 600, 
                            fontFamily: 'Inter, sans-serif' 
                          }} 
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#374151' }}>
                        {record.host}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#374151' }}>
                        <Box sx={{ 
                          wordBreak: 'break-all', 
                          maxHeight: 120, 
                          overflow: 'auto',
                          bgcolor: '#ffffff',
                          p: 1,
                          borderRadius: 1,
                          border: '1px solid #e5e7eb',
                        }}>
                          {record.value}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '0.875rem' }}>
                        {record.ttl || '1 Hour'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#374151', textAlign: 'center' }}>
                        {record.priority !== undefined ? record.priority : '—'}
                      </TableCell>
                      <TableCell>
                        {verificationStatus[getVerificationKey(record)] ? (
                          <Chip icon={<CheckIcon />} label="Verified" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534' }} />
                        ) : (
                          <Chip icon={<WarningIcon />} label="Pending" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={copied === `${index}` ? 'Copied!' : 'Copy value'}>
                          <IconButton 
                            onClick={() => copyToClipboard(record.value, `${index}`)} 
                            size="small" 
                            sx={{ color: copied === `${index}` ? '#22c55e' : '#6b7280' }}
                          >
                            {copied === `${index}` ? <CheckIcon /> : <CopyIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );})
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Quick Access to Registrars - SECOND */}
          <Card sx={{ mb: 4, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontWeight: 600, color: '#111827', mb: 2, fontFamily: 'Inter, sans-serif' }}>
                🚀 Quick Access to DNS Management
              </Typography>
              <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', mb: 3, fontFamily: 'Inter, sans-serif' }}>
                Select your domain registrar to open DNS settings directly. After logging in, add the records shown above.
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {Object.entries(registrars).map(([key, reg]) => (
                  <Button
                    key={key}
                    variant="outlined"
                    onClick={() => openRegistrarDns(key)}
                    startIcon={<span style={{ fontSize: '1.2rem' }}>{reg.logo}</span>}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      borderColor: detectedRegistrar === key ? reg.color : '#e5e7eb',
                      color: detectedRegistrar === key ? reg.color : '#374151',
                      bgcolor: detectedRegistrar === key ? `${reg.color}10` : 'transparent',
                      '&:hover': { borderColor: reg.color, bgcolor: `${reg.color}10` },
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      py: 0.75,
                      px: 1.5,
                    }}
                  >
                    {reg.name}
                  </Button>
                ))}
              </Box>

              {detectedRegistrar && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  <strong>Detected:</strong> Your domain appears to be managed by <strong>{registrars[detectedRegistrar]?.name}</strong>. 
                  Click the button above to open DNS settings.
                </Alert>
              )}

              {nameservers.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151', mb: 1 }}>
                    Detected Nameservers:
                  </Typography>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>
                    {nameservers.slice(0, 4).map((ns, i) => <div key={i}>{ns}</div>)}
                    {nameservers.length > 4 && <div>...and {nameservers.length - 4} more</div>}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleSkip}
              sx={{ px: 4, py: 1.5, borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#dc2626', bgcolor: 'transparent' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
            >
              Skip for Now
            </Button>
            <Button
              variant="outlined"
              onClick={verifyDns}
              disabled={verifying}
              startIcon={verifying ? <CircularProgress size={18} /> : <RefreshIcon />}
              sx={{ px: 4, py: 1.5, borderColor: '#dc2626', color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
            >
              {verifying ? 'Verifying...' : 'Verify DNS'}
            </Button>
            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={loading}
              sx={{ px: 4, py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </SetupLayout>
  );
};

export default DNSSetup;
